import { useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";

const ACTIVITY_KEY = "idle:lastActivity";
const FORCE_LOGOUT_KEY = "idle:forceLogoutAt";

type Props = {
  timeoutMinutes?: number;
  postLogoutRedirectUri?: string;
};

export default function IdleLogout({
  timeoutMinutes = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES) || 15,
  postLogoutRedirectUri = "/",
}: Props) {
  const { instance, accounts } = useMsal();
  const timerRef = useRef<number | null>(null);
  const isLoggingOutRef = useRef(false);
  const timeoutMs = Math.max(1, timeoutMinutes) * 60_000;

  useEffect(() => {
    // Don't run idle logout if no user is logged in
    if (!accounts || accounts.length === 0) {
      return;
    }

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("idle-logout");
      bc.onmessage = (e) => {
        if (e?.data === "force-logout") doLogout();
        if (e?.data === "activity") scheduleFromLastActivity();
      };
    } catch {
      bc = null;
    }

    const now = Date.now();
    if (!localStorage.getItem(ACTIVITY_KEY)) {
      localStorage.setItem(ACTIVITY_KEY, String(now));
    }
    scheduleFromLastActivity();

    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      const t = Date.now();
      localStorage.setItem(ACTIVITY_KEY, String(t));
      bc?.postMessage("activity");
      resetTimer(timeoutMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") onActivity();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === FORCE_LOGOUT_KEY && e.newValue) doLogout();
      if (e.key === ACTIVITY_KEY && e.newValue) scheduleFromLastActivity();
    };

    const events: (keyof DocumentEventMap | keyof WindowEventMap)[] = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    events.forEach((evt) => window.addEventListener(evt as any, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);

    function scheduleFromLastActivity() {
      const last = Number(localStorage.getItem(ACTIVITY_KEY) || Date.now());
      const remaining = timeoutMs - (Date.now() - last);
      if (remaining <= 0) {
        doLogout();
      } else {
        resetTimer(remaining);
      }
    }

    function resetTimer(ms: number) {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => doLogout(), ms);
    }

    async function doLogout() {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;
      try {
        localStorage.setItem(FORCE_LOGOUT_KEY, String(Date.now()));
        bc?.postMessage("force-logout");
        // Clear activity tracking
        localStorage.removeItem(ACTIVITY_KEY);
        localStorage.removeItem(FORCE_LOGOUT_KEY);
      } catch {}
      try {
        const account = instance.getActiveAccount() ?? accounts[0];
        // Clear cache first
        instance.clearCache();
        // Use popup to avoid account selection screen
        await instance.logoutPopup({
          account: account,
          mainWindowRedirectUri: postLogoutRedirectUri,
        });
        // Redirect after popup closes
        window.location.assign(postLogoutRedirectUri);
      } catch {
        try { instance.clearCache(); } catch {}
        window.location.assign(postLogoutRedirectUri);
      }
    }

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      events.forEach((evt) => window.removeEventListener(evt as any, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      try { bc?.close(); } catch {}
    };
  }, [accounts, instance, timeoutMs, postLogoutRedirectUri]);

  return null;
}
